<template>
  <div class="faq-view">
    <div class="container">
      <h1>{{ t('faq.title') }}</h1>
      <p class="subtitle">{{ t('faq.subtitle') }}</p>
      
      <div class="faq-list">
        <el-collapse v-model="activeNames" accordion>
          <el-collapse-item name="1">
            <template #title>
              <span class="faq-question">{{ t('faq.q1.question') }}</span>
            </template>
            <div class="faq-answer">
              <p>{{ t('faq.q1.answer') }}</p>
            </div>
          </el-collapse-item>
          
          <el-collapse-item name="2">
            <template #title>
              <span class="faq-question">{{ t('faq.q2.question') }}</span>
            </template>
            <div class="faq-answer">
              <p>{{ t('faq.q2.answer_intro') }}</p>
              <ul>
                <li><strong>{{ t('faq.q2.auto_title') }}:</strong> {{ t('faq.q2.auto_desc') }}</li>
                <li><strong>{{ t('faq.q2.manual_title') }}:</strong> {{ t('faq.q2.manual_desc') }}</li>
              </ul>
            </div>
          </el-collapse-item>
          
          <el-collapse-item name="3">
            <template #title>
              <span class="faq-question">{{ t('faq.q3.question') }}</span>
            </template>
            <div class="faq-answer">
              <p>{{ t('faq.q3.answer') }}</p>
              <ul>
                <li>{{ t('faq.q3.item1') }}</li>
                <li>{{ t('faq.q3.item2') }}</li>
                <li>{{ t('faq.q3.item3') }}</li>
              </ul>
            </div>
          </el-collapse-item>
          
          <el-collapse-item name="4">
            <template #title>
              <span class="faq-question">{{ t('faq.q4.question') }}</span>
            </template>
            <div class="faq-answer">
              <p>{{ t('faq.q4.answer') }}</p>
            </div>
          </el-collapse-item>
          
          <el-collapse-item name="5">
            <template #title>
              <span class="faq-question">{{ t('faq.q5.question') }}</span>
            </template>
            <div class="faq-answer">
              <p>{{ t('faq.q5.answer') }}</p>
            </div>
          </el-collapse-item>
          
          <el-collapse-item name="6">
            <template #title>
              <span class="faq-question">{{ t('faq.q6.question') }}</span>
            </template>
            <div class="faq-answer">
              <p>{{ t('faq.q6.answer') }}</p>
            </div>
          </el-collapse-item>
        </el-collapse>
      </div>
      
      <div class="faq-contact">
        <h2>{{ t('faq.more_questions') }}</h2>
        <p>{{ t('faq.contact_us') }}</p>
        <router-link to="/contact" class="contact-btn">
          {{ t('faq.contact_button') }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from '@/i18n'

const { t } = useI18n()
const activeNames = ref(['1'])
</script>

<style scoped>
.faq-view {
  min-height: calc(100vh - 200px);
  padding: var(--m2v-space-12) var(--m2v-space-4);
  background: var(--m2v-bg-primary);
}

.container {
  max-width: 800px;
  margin: 0 auto;
}

h1 {
  font-family: var(--m2v-font-display);
  font-size: 2.5rem;
  color: var(--m2v-text-primary);
  margin-bottom: var(--m2v-space-2);
  text-align: center;
}

.subtitle {
  color: var(--m2v-text-muted);
  text-align: center;
  margin-bottom: var(--m2v-space-8);
  font-size: 1.1rem;
}

.faq-list {
  margin-bottom: var(--m2v-space-12);
}

:deep(.el-collapse) {
  border: none;
  --el-collapse-header-bg-color: var(--m2v-bg-secondary);
  --el-collapse-content-bg-color: var(--m2v-bg-primary);
}

:deep(.el-collapse-item) {
  margin-bottom: var(--m2v-space-3);
  border-radius: var(--m2v-radius-lg);
  overflow: hidden;
  border: 1px solid var(--m2v-border);
  background: var(--m2v-bg-secondary);
}

:deep(.el-collapse-item__header) {
  padding: var(--m2v-space-4) var(--m2v-space-5);
  border: none;
  height: auto;
  line-height: 1.5;
  background: var(--m2v-bg-secondary);
  color: var(--m2v-text-primary);
}

:deep(.el-collapse-item__header.is-active) {
  border-bottom: 1px solid var(--m2v-border);
}

:deep(.el-collapse-item__wrap) {
  border: none;
  background: var(--m2v-bg-primary);
}

:deep(.el-collapse-item__content) {
  padding: var(--m2v-space-5);
  color: var(--m2v-text-secondary);
}

:deep(.el-collapse-item__arrow) {
  color: var(--m2v-primary);
}

.faq-question {
  font-weight: 600;
  font-size: 1.05rem;
  color: var(--m2v-text-primary);
}

.faq-answer {
  line-height: 1.7;
}

.faq-answer p {
  margin-bottom: var(--m2v-space-3);
}

.faq-answer ul {
  margin: var(--m2v-space-3) 0;
  padding-left: var(--m2v-space-5);
}

.faq-answer li {
  margin-bottom: var(--m2v-space-2);
}

.faq-answer strong {
  color: var(--m2v-primary);
}

.faq-contact {
  text-align: center;
  padding: var(--m2v-space-8);
  background: var(--m2v-bg-secondary);
  border-radius: var(--m2v-radius-xl);
  border: 1px solid var(--m2v-border);
}

.faq-contact h2 {
  font-family: var(--m2v-font-display);
  font-size: 1.5rem;
  color: var(--m2v-text-primary);
  margin-bottom: var(--m2v-space-2);
}

.faq-contact p {
  color: var(--m2v-text-muted);
  margin-bottom: var(--m2v-space-4);
}

.contact-btn {
  display: inline-block;
  padding: var(--m2v-space-3) var(--m2v-space-6);
  background: var(--m2v-primary);
  color: white;
  border-radius: var(--m2v-radius-lg);
  font-weight: 600;
  text-decoration: none;
  transition: all var(--m2v-transition-normal);
}

.contact-btn:hover {
  background: var(--m2v-primary-dark);
  transform: translateY(-2px);
}

@media (max-width: 768px) {
  h1 {
    font-size: 2rem;
  }
  
  .faq-question {
    font-size: 0.95rem;
  }
}
</style>
